import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Zap, Loader2, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";

export const Route = createFileRoute("/auth")({ component: AuthPage });

function AuthPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/portal" });
    });
  }, [navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password: pw,
          options: {
            emailRedirectTo: `${window.location.origin}/portal`,
            data: { full_name: name },
          },
        });
        if (error) throw error;
        toast.success("Account creato", { description: "Controlla l'email per confermare (se richiesto)." });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
        if (error) throw error;
      }
      await router.invalidate();
      navigate({ to: "/portal" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Errore";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white relative overflow-hidden flex items-center justify-center px-4 py-16">
      <Toaster theme="dark" position="top-right" />
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-fuchsia-600/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-cyan-500/20 blur-3xl" />
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center">
            <Zap className="h-5 w-5 text-black" />
          </div>
          <span className="font-semibold tracking-tight text-lg">FlashWeb Portal</span>
        </Link>

        <Card className="p-8 bg-white/[0.03] border-white/10 backdrop-blur-xl rounded-3xl">
          <div className="flex gap-1 rounded-full bg-white/5 p-1 mb-6">
            {(["signin", "signup"] as const).map((m) => (
              <button
                key={m} type="button" onClick={() => setMode(m)}
                className={`flex-1 rounded-full py-2 text-sm font-medium transition ${
                  mode === m ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {m === "signin" ? "Accedi" : "Registrati"}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <Label>Nome completo</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-white/5 border-white/10" />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black hover:opacity-90 rounded-full h-11 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><span>{mode === "signin" ? "Accedi" : "Crea account"}</span><ArrowRight className="ml-1.5 h-4 w-4" /></>)}
            </Button>
          </form>

          <p className="text-xs text-white/40 text-center mt-6">
            Il portale è riservato ai clienti FlashWeb. <Link to="/" className="text-white/70 hover:text-white underline">Torna al sito</Link>
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
